<template>
  <div class="checkpoint-drop-down-menu">
    <CheckpointOperationsLockIcon v-if="locked" />
    <DropDownMenu
      v-else
      :dropdownItems="visibleMenuItems"
      :isOpen="isMenuOpen"
      :tip-side="tipSide"
      @onClick="handleMenuClick"
      :menuWidth="true"
      :translate-x-offset="translateXOffset"
      :translate-y-offset="translateYOffset"
    >
      <template #activator>
        <div class="icon-container" :class="{ horizontalMenu: props.horizontal }">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-svg"
          >
            <path
              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </DropDownMenu>
  </div>
</template>

<script setup>
import { ref, computed, reactive, watch } from "vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import CheckpointOperationsLockIcon from "@components/CheckpointOperationsLockIcon/CheckpointOperationsLockIcon.vue";

const props = defineProps({
  status: {
    type: String,
    default: "Draft",
    validator: (s) => ["Draft", "Active", "Retired", "Archived"].includes(s),
  },
  menuOpen: {
    type: Boolean,
    default: false,
  },
  horizontal: {
    type: Boolean,
    default: false,
  },
  activateDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * When true, replace the ⋮ menu with a static lock icon (Rule 5).
   * Shown for every viewer while any Access holds the edit lock — including the holder.
   */
  locked: {
    type: Boolean,
    default: false,
  },
  /**
   * Override menu placement tip. `right` = panel opens left of the ⋮ (needed at the
   * table’s trailing edge so the panel is not clipped by the page).
   */
  tipSide: {
    type: String,
    default: null,
    validator: (v) => v == null || v === "" || ["left", "right"].includes(v),
  },
});

const emit = defineEmits(["action"]);
const isMenuOpen = ref(props.menuOpen);
const showDialog = reactive({
  activate: false,
  delete: false,
  retire: false,
  reactivate: false,
  move: false,
  archive: false,
  unarchive: false,
});
watch(
  () => props.menuOpen,
  (newVal) => {
    isMenuOpen.value = newVal;
  }
);
const visibleMenuItems = computed(() =>
  [
    props.status === "Draft" && {
      name: "Activate",
      value: "activate",
      icon: "icons/dropdown/activate.svg",
      destructive: false,
      disabled: props.activateDisabled,
    },
    props.status === "Draft" && {
      name: "Delete",
      value: "delete",
      icon: "icons/dropdown/trash.svg",
      destructive: true,
      negative: true,
    },
    props.status === "Active" && {
      name: "Retire",
      value: "retire",
      icon: "icons/dropdown/activate.svg",
      destructive: false,
    },
    props.status === "Retired" && {
      name: "Reactivate",
      value: "reactivate",
      icon: "icons/dropdown/activate.svg",
      destructive: false,
    },
    {
      name: "Move",
      value: "move",
      icon: "icons/dropdown/arrow-square-right.svg",
      destructive: false,
    },
    props.status === "Retired" && {
      name: "Archive",
      value: "archive",
      icon: "icons/dropdown/archive-new.svg",
      destructive: false,
    },
    props.status === "Archived" && {
      name: "Unarchive",
      value: "unarchive",
      icon: "icons/dropdown/activate.svg",
      destructive: false,
    },
  ].filter(Boolean)
);
/** Header toolbar opens left of the ⋮ (tip on right); table row default opens right of the ⋮ (tip on left). */
const tipSide = computed(() => {
  if (props.tipSide === "left" || props.tipSide === "right") return props.tipSide;
  return props.horizontal ? "right" : "left";
});
const translateXOffset = computed(() => (tipSide.value === "right" ? 0 : 12));
const translateYOffset = computed(() => (props.horizontal ? 20 : 30));

function handleMenuClick(item) {
  if (item?.disabled) {
    return;
  }
  resetDialogs();
  emit("action", item);
  switch (item.value) {
    case "activate":
      showDialog.activate = true;
      break;
    case "delete":
      showDialog.delete = true;
      break;
    case "retire":
      showDialog.retire = true;
      break;
    case "reactivate":
      showDialog.reactivate = true;
      break;
    case "move":
      showDialog.move = true;
      break;
    case "archive":
      showDialog.archive = true;
      break;
    case "unarchive":
      showDialog.unarchive = true;
      break;
  }
}
function resetDialogs() {
  Object.keys(showDialog).forEach((k) => {
    showDialog[k] = false;
  });
}
</script>
<style scoped>
.checkpoint-drop-down-menu {
  width: 25%;
  text-align: end;
}
.horizontalMenu {
  transform: rotate(90deg);
}
.horizontalMenu.icon-container {
  top: 6px !important;
}
.horizontalMenu.icon-container:hover {
  background-color: transparent;
  box-shadow: none;
}
</style>
